/**
 * Scheduling API
 *
 * API endpoints for scheduling management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type {
  DeleteSchedulingArgs,
  EditSchedulingArgs,
  GetDeletedSchedulingsByOrder,
  GetSchedulingsByOrderArgs,
  Scheduling,
  SchedulingParams,
  TransformedGetScheduling
} from "./scheduling.types";
import { addDaysLeftToScheduling } from "./scheduling.utils";
import { editSchedulingArgsArraySchema } from "./schemas";

/**
 * Scheduling API endpoints
 * Provides CRUD operations for scheduling management
 */
export const schedulingApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get all schedulings with optional filters
     * @param params - Optional filter parameters
     * @returns Array of schedulings
     */
    getSchedulings: build.query<Scheduling[], Partial<SchedulingParams> | undefined, Scheduling[]>({
      query: (params = {}) => {
        const { ids = {}, groupById, ...rest } = params;
        const transformIds = Object.fromEntries(Object.entries(ids).map(([key, value]) => [key, value ? String(value) : undefined]));
        const { company, driver, truck, order, customer } = transformIds;

        return {
          url: "/scheduling",
          params: {
            companyId: company,
            driverId: driver,
            truckId: truck,
            orderId: order,
            customerId: customer,
            groupById: groupById ?? undefined,
            ...rest
          }
        };
      },
      transformResponse: (response) => {
        return response.map((item) => ({
          ...item,
          order: { ...item.order, code: item.order.code ?? "N/A" }
        }));
      }
    }),

    /**
     * Get a single scheduling by ID
     * @param id - Scheduling ID
     * @returns Scheduling details
     */
    getScheduling: build.query<Scheduling, { id: number }>({
      query: ({ id }) => `/scheduling/${id}`,
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando carregamento...",
          success: "Carregamento carregado com sucesso!",
          error: "Erro ao carregar carregamento!"
        });
      }
    }),

    /**
     * Create or update scheduling records
     * @param payload - Array of scheduling data
     * @returns void
     */
    saveScheduling: build.mutation<void, EditSchedulingArgs[]>({
      query: (payload) => {
        const body = payload.map((item) => ({
          ...item,
          id: item.id ?? 0,
          truck: { ...item.truck, id: Number(item.truck.id) },
          driver: { ...item.driver, id: Number(item.driver.id) },
          company: { ...item.company, id: Number(item.company.id) },
          order: { ...item.order, id: Number(item.order.id) }
        }));

        return {
          url: `/scheduling/save`,
          method: "POST",
          body
        };
      },
      argSchema: editSchedulingArgsArraySchema,
      onQueryStarted: (payload, { queryFulfilled }) => {
        const count = payload.length;
        const isMultiple = count > 1;

        toast.promise(queryFulfilled, {
          loading: isMultiple ? `Salvando ${count} carregamentos...` : "Salvando carregamento...",
          success: isMultiple ? `${count} carregamentos salvos com sucesso!` : "Carregamento salvo com sucesso!",
          error: isMultiple ? "Erro ao salvar carregamentos!" : "Erro ao salvar carregamento!"
        });
      }
    }),

    /**
     * Delete a scheduling record
     * @param id - Scheduling ID
     * @param orderId - Optional order ID for cache invalidation
     * @returns void
     */
    deleteScheduling: build.mutation<void, DeleteSchedulingArgs>({
      query: ({ id }) => ({
        url: `/scheduling/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo carregamento...",
          success: "Carregamento excluído com sucesso!",
          error: (err) => {
            const error = err.error?.data?.message;

            if (!error) return "Erro desconhecido!";

            return error;
          }
        });
      }
    }),

    /**
     * Get schedulings by order ID
     * @param id - Order ID
     * @param includeGroupTable - Whether to include grouped table data
     * @returns Schedulings with optional table grouping
     */
    getSchedulingsByOrder: build.query<TransformedGetScheduling, GetSchedulingsByOrderArgs, Scheduling[]>({
      query: ({ id }) => `/scheduling/order/${id}`,
      async onQueryStarted({ id, includeGroupTable = false }, { dispatch, queryFulfilled }) {
        const deletedSchedulingByOrder = dispatch(schedulingApi.endpoints.getDeletedSchedulingsByOrder.initiate({ id }, { forceRefetch: true, subscribe: false }));

        const [, deletedSchedulingByOrderResult] = await Promise.all([queryFulfilled, deletedSchedulingByOrder]);

        dispatch(
          schedulingApi.util.updateQueryData("getSchedulingsByOrder", { id, includeGroupTable }, (draft) => {
            if (draft.table && deletedSchedulingByOrderResult.data) {
              draft.table.dropped.items = deletedSchedulingByOrderResult.data;
              draft.table.dropped.length = deletedSchedulingByOrderResult.data.length;
            }
          })
        );
      },
      transformResponse: (response, meta, { includeGroupTable = false }) => {
        const responseWithDaysLeft = response.map((item) => {
          const data = addDaysLeftToScheduling(item);
          return { ...data, qtyTransported: data.qtyTransported ?? 0 };
        });

        if (!includeGroupTable) {
          return {
            data: responseWithDaysLeft
          };
        }

        const table = responseWithDaysLeft.reduce<NonNullable<TransformedGetScheduling["table"]>>(
          (acc, curr) => {
            if (curr.status) {
              acc.finished.items.push(curr);
              acc.finished.length++;
              return acc;
            }

            if (!curr.expedInitialDate) {
              acc.awaiting.items.push(curr);
              acc.awaiting.length++;
              return acc;
            }

            // Items que têm expedInitialDate mas não têm status são considerados dropped
            // Os dados reais de dropped virão da query separada getDeletedSchedulingsByOrder
            return acc;
          },
          {
            awaiting: { label: "Aguardando", length: 0, items: [] },
            finished: { label: "Concluídos", length: 0, items: [] },
            dropped: { label: "Desistentes", length: 0, items: [] }
          }
        );

        return { data: responseWithDaysLeft, table };
      }
    }),

    /**
     * Get deleted schedulings by order ID
     * @param id - Order ID
     * @returns Array of deleted schedulings with days left
     */
    getDeletedSchedulingsByOrder: build.query<GetDeletedSchedulingsByOrder, { id: number }, Scheduling[]>({
      query: ({ id }) => `/schedulingDeleted/order/${id}`,
      transformResponse: (response) => {
        const responseWithDaysLeft = response.map(addDaysLeftToScheduling);
        return responseWithDaysLeft.map((item) => ({
          ...item,
          qtyTransported: typeof item.qtyTransported === "number" ? item.qtyTransported : Number(item.qtyTransported) || 0
        }));
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
schedulingApi.enhanceEndpoints({
  addTagTypes: ["Scheduling", "SchedulingByOrder"],
  endpoints: {
    getSchedulings: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "Scheduling" as const, id })), { type: "Scheduling", id: "LIST" }] : [{ type: "Scheduling", id: "LIST" }]
    },
    getScheduling: {
      providesTags: (result, error, { id }) => [{ type: "Scheduling", id }]
    },
    saveScheduling: {
      invalidatesTags: (_result, error) => {
        if (!error) return ["SchedulingByOrder", "Orders"];
        return [];
      }
    },
    deleteScheduling: {
      invalidatesTags: (result, error) => {
        if (!error) return ["SchedulingByOrder", "Orders"];
        return [];
      }
    },
    getSchedulingsByOrder: {
      providesTags: (result, error, { id }) =>
        result ? [...result.data.map(({ id }) => ({ type: "Scheduling" as const, id })), { type: "SchedulingByOrder", id }] : [{ type: "SchedulingByOrder", id }]
    },
    getDeletedSchedulingsByOrder: {
      providesTags: (result, error, { id }) =>
        result ? [...result.map(({ id }) => ({ type: "Scheduling" as const, id })), { type: "SchedulingByOrder", id }] : [{ type: "SchedulingByOrder", id }]
    }
  }
});
